import { ContentCard } from "@/components/content-card";
import { MediaUploader, type UploadData } from "@/components/MediaUploader";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useCompleteUpload } from "@/hooks/useContentUpload";
import { coachService } from "@/lib/api/coachService";
import { useAuth } from "@/lib/auth";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { Coach, Content, ContentStatus, ContentType } from "@/lib/schema";
import { contentStatuses, contentTypes } from "@/lib/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  Compass,
  FileAudio,
  FileText,
  FileVideo,
  Filter,
  Grid,
  Library,
  List,
  MessageSquareQuote,
  Plus,
  Search,
} from "lucide-react";
import { useState } from "react";

const contentTypeLabels: Partial<Record<ContentType, string>> = {
  blog: "Blog",
  audio: "Audio",
  video: "Video",
  journey: "Journey",
  workbook_question: "Workbook Question",
  quote: "Quote",
};

export default function ContentLibrary() {
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingContent, setEditingContent] = useState<Content | null>(null);
  const [selectedType, setSelectedType] = useState<ContentType>("blog");
  const [uploadData, setUploadData] = useState<UploadData | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploadComplete, setIsUploadComplete] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const { data: contentList, isLoading } = useQuery<Content[]>({
    queryKey: ["/api/content"],
  });

  const { data: coaches } = useQuery<Coach[]>({
    queryKey: ["/api/coaches"],
    queryFn: () => coachService.getCoaches().then((r) => r.coaches),
  });

  const resetDialogState = () => {
    setIsDialogOpen(false);
    setEditingContent(null);
    setSelectedType("blog");
    setUploadData(null);
    setUploadProgress(0);
    setIsUploadComplete(false);
  };

  const createMutation = useMutation({
    mutationFn: (data: Partial<Content>) =>
      apiRequest("POST", "/api/content", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/content"] });
      resetDialogState();
      toast({ title: "Content created successfully" });
    },
    onError: () => {
      toast({ title: "Failed to create content", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Content> }) =>
      apiRequest("PATCH", `/api/content/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/content"] });
      resetDialogState();
      toast({ title: "Content updated successfully" });
    },
    onError: () => {
      toast({ title: "Failed to update content", variant: "destructive" });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("PATCH", `/api/content/${id}`, { status: "archived" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/content"] });
      toast({ title: "Content archived" });
    },
    onError: () => {
      toast({ title: "Failed to archive content", variant: "destructive" });
    },
  });

  const completeUploadMutation = useCompleteUpload();

  const filteredContent = contentList?.filter((item) => {
    const matchesSearch =
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.coachName?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === "all" || item.type === typeFilter;
    const matchesStatus =
      statusFilter === "all" || item.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const coach = coaches?.find((c) => c.id === formData.get("coachId"));
    const contentType = formData.get("type") as ContentType;

    // Handle audio/video types with multipart upload
    if (
      (contentType === "audio" || contentType === "video") &&
      !editingContent
    ) {
      if (!uploadData) {
        toast({
          title: "Upload required",
          description: "Please upload a file first",
          variant: "destructive",
        });
        return;
      }

      completeUploadMutation.mutate({
        type: contentType,
        data: {
          uploadId: uploadData.uploadId,
          key: uploadData.key,
          parts: uploadData.parts,
          title: formData.get("title") as string,
          description: (formData.get("description") as string) || undefined,
          userId: user?.id || "",
        },
      });
      return;
    }

    // Handle other content types (blog, journey, etc.)
    const data: Partial<Content> = {
      title: formData.get("title") as string,
      type: contentType,
      description: formData.get("description") as string,
      coachId: formData.get("coachId") as string,
      coachName: coach?.name || null,
      status: formData.get("status") as ContentStatus,
      lastModified: new Date().toISOString(),
    };

    // Add type-specific fields
    switch (contentType) {
      case "blog":
        data.body = formData.get("body") as string;
        break;
      case "audio":
        data.mediaUrl = formData.get("mediaUrl") as string;
        data.duration = parseInt(formData.get("duration") as string) || null;
        break;
      case "video":
        data.mediaUrl = formData.get("mediaUrl") as string;
        data.duration = parseInt(formData.get("duration") as string) || null;
        break;
      case "journey":
        data.journeyDay =
          parseInt(formData.get("journeyDay") as string) || null;
        data.totalDays = parseInt(formData.get("totalDays") as string) || null;
        data.body = formData.get("body") as string;
        break;
      case "workbook_question":
        data.body = formData.get("body") as string;
        break;
      case "quote":
        data.body = formData.get("body") as string;
        break;
    }

    // Handle tags
    const tagsInput = formData.get("tags") as string;
    if (tagsInput) {
      data.tags = tagsInput
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
    }

    if (editingContent) {
      updateMutation.mutate({ id: editingContent.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (content: Content) => {
    setEditingContent(content);
    setSelectedType(content.type as ContentType);
    setIsDialogOpen(true);
  };

  const handleArchive = (content: Content) => {
    archiveMutation.mutate(content.id);
  };

  return (
    <div className="min-h-screen">
      {/* Editorial Header */}
      <section className="px-8 lg:px-12 pt-12 pb-8">
        <div className="max-w-6xl">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <p className="text-sm font-medium text-primary/70 tracking-wide uppercase mb-3">
                Content Garden
              </p>
              <h1 className="text-4xl lg:text-5xl font-serif font-light tracking-tight text-foreground/90 leading-tight">
                Your Ritual Library
              </h1>
              <p className="text-lg text-muted-foreground mt-4 max-w-xl leading-relaxed font-light">
                Nurture and curate wellness content that guides your community's
                journey.
              </p>
            </div>
            <Button
              onClick={() => setIsDialogOpen(true)}
              data-testid="button-add-content"
              className="shrink-0"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Content
            </Button>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="px-8 lg:px-12 pb-8">
        <div className="max-w-6xl">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search content or coach..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="input-search-content"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger
                className="w-[180px]"
                data-testid="select-type-filter"
              >
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {contentTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {contentTypeLabels[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger
                className="w-[150px]"
                data-testid="select-status-filter"
              >
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {contentStatuses.map((status) => (
                  <SelectItem
                    key={status}
                    value={status}
                    className="capitalize"
                  >
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-1 border rounded-md p-1">
              <Button
                variant={viewMode === "grid" ? "secondary" : "ghost"}
                size="icon"
                onClick={() => setViewMode("grid")}
                data-testid="button-grid-view"
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "secondary" : "ghost"}
                size="icon"
                onClick={() => setViewMode("list")}
                data-testid="button-list-view"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="h-32 rounded-lg bg-muted animate-pulse"
                />
              ))}
            </div>
          ) : filteredContent?.length === 0 ? (
            <div className="text-center py-12">
              <Library className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No content found</h3>
              <p className="text-muted-foreground mt-1">
                {searchQuery || typeFilter !== "all" || statusFilter !== "all"
                  ? "Try adjusting your filters"
                  : "Your content library is ready to grow"}
              </p>
            </div>
          ) : (
            <div
              className={
                viewMode === "grid"
                  ? "grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
                  : "space-y-4"
              }
            >
              {filteredContent?.map((content) => (
                <ContentCard
                  key={content.id}
                  content={content}
                  onEdit={handleEdit}
                  onArchive={handleArchive}
                />
              ))}
            </div>
          )}

          <Dialog
            open={isDialogOpen}
            onOpenChange={(open) => !open && resetDialogState()}
          >
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingContent ? "Edit Content" : "Add New Content"}
                </DialogTitle>
                <DialogDescription>
                  {editingContent
                    ? "Update the content details below"
                    : "Create a new wellness ritual or content piece"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Basic Info Section */}
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    name="title"
                    required
                    defaultValue={editingContent?.title}
                    data-testid="input-content-title"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="type">Content Type</Label>
                  <Select
                    name="type"
                    value={selectedType}
                    onValueChange={(value) =>
                      setSelectedType(value as ContentType)
                    }
                  >
                    <SelectTrigger data-testid="select-content-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {contentTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {contentTypeLabels[type]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    name="description"
                    defaultValue={editingContent?.description || ""}
                    placeholder="Brief description of this content"
                    data-testid="input-content-description"
                  />
                </div>

                <Separator />

                {/* Type-Specific Fields */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    {selectedType === "blog" && (
                      <FileText className="h-4 w-4" />
                    )}
                    {selectedType === "audio" && (
                      <FileAudio className="h-4 w-4" />
                    )}
                    {selectedType === "video" && (
                      <FileVideo className="h-4 w-4" />
                    )}
                    {selectedType === "journey" && (
                      <Compass className="h-4 w-4" />
                    )}
                    {selectedType === "workbook_question" && (
                      <BookOpen className="h-4 w-4" />
                    )}
                    {selectedType === "quote" && (
                      <MessageSquareQuote className="h-4 w-4" />
                    )}
                    <span>{contentTypeLabels[selectedType]} Content</span>
                  </div>

                  {/* Blog - Rich text body */}
                  {selectedType === "blog" && (
                    <div className="space-y-2">
                      <Label htmlFor="body">Article Content</Label>
                      <Textarea
                        id="body"
                        name="body"
                        rows={8}
                        required
                        defaultValue={editingContent?.body || ""}
                        placeholder="Write your blog article content here..."
                        className="min-h-[200px]"
                        data-testid="input-content-body"
                      />
                      <p className="text-xs text-muted-foreground">
                        You can use markdown formatting for rich text
                      </p>
                    </div>
                  )}

                  {/* Audio - Upload via MediaUploader */}
                  {selectedType === "audio" && (
                    <>
                      <div className="space-y-2">
                        <Label>Audio File Upload</Label>
                        {!editingContent ? (
                          <>
                            <MediaUploader
                              type="audio"
                              onUploadProgress={setUploadProgress}
                              onUploadSuccess={(data) => {
                                setUploadData(data);
                                setIsUploadComplete(true);
                                toast({
                                  title: "Upload complete",
                                  description: `File uploaded (${uploadProgress}%). Click Create to save.`,
                                });
                              }}
                              onUploadError={(error) => {
                                toast({
                                  title: "Upload failed",
                                  description: error.message,
                                  variant: "destructive",
                                });
                              }}
                            />
                            {uploadProgress > 0 && uploadProgress < 100 && (
                              <p className="text-sm text-muted-foreground">
                                Uploading: {uploadProgress}%
                              </p>
                            )}
                          </>
                        ) : (
                          <Input
                            name="mediaUrl"
                            defaultValue={editingContent?.mediaUrl || ""}
                            placeholder="Media URL"
                          />
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="duration">Duration (seconds)</Label>
                        <Input
                          id="duration"
                          name="duration"
                          type="number"
                          min="1"
                          defaultValue={editingContent?.duration || ""}
                          placeholder="300"
                          data-testid="input-content-duration"
                        />
                      </div>
                    </>
                  )}

                  {/* Video - Upload via MediaUploader */}
                  {selectedType === "video" && (
                    <>
                      <div className="space-y-2">
                        <Label>Video File Upload</Label>
                        {!editingContent ? (
                          <>
                            <MediaUploader
                              type="video"
                              onUploadProgress={setUploadProgress}
                              onUploadSuccess={(data) => {
                                setUploadData(data);
                                setIsUploadComplete(true);
                                toast({
                                  title: "Upload complete",
                                  description: `File uploaded (${uploadProgress}%). Click Create to save.`,
                                });
                              }}
                              onUploadError={(error) => {
                                toast({
                                  title: "Upload failed",
                                  description: error.message,
                                  variant: "destructive",
                                });
                              }}
                            />
                            {uploadProgress > 0 && uploadProgress < 100 && (
                              <p className="text-sm text-muted-foreground">
                                Uploading: {uploadProgress}%
                              </p>
                            )}
                          </>
                        ) : (
                          <Input
                            name="mediaUrl"
                            defaultValue={editingContent?.mediaUrl || ""}
                            placeholder="Media URL"
                          />
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="duration">Duration (seconds)</Label>
                        <Input
                          id="duration"
                          name="duration"
                          type="number"
                          min="1"
                          defaultValue={editingContent?.duration || ""}
                          placeholder="600"
                          data-testid="input-content-duration"
                        />
                      </div>
                    </>
                  )}

                  {/* Journey - Day structure */}
                  {selectedType === "journey" && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="journeyDay">Day Number</Label>
                          <Input
                            id="journeyDay"
                            name="journeyDay"
                            type="number"
                            min="1"
                            required
                            defaultValue={editingContent?.journeyDay || ""}
                            placeholder="1"
                            data-testid="input-content-journey-day"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="totalDays">
                            Total Days in Journey
                          </Label>
                          <Input
                            id="totalDays"
                            name="totalDays"
                            type="number"
                            min="1"
                            required
                            defaultValue={editingContent?.totalDays || ""}
                            placeholder="7"
                            data-testid="input-content-total-days"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="body">Day Content</Label>
                        <Textarea
                          id="body"
                          name="body"
                          rows={6}
                          required
                          defaultValue={editingContent?.body || ""}
                          placeholder="Describe this day's activities and learnings..."
                          data-testid="input-content-body"
                        />
                      </div>
                    </>
                  )}

                  {/* Workbook Question */}
                  {selectedType === "workbook_question" && (
                    <div className="space-y-2">
                      <Label htmlFor="body">Question</Label>
                      <Textarea
                        id="body"
                        name="body"
                        rows={4}
                        required
                        defaultValue={editingContent?.body || ""}
                        placeholder="Enter the reflection question for the workbook..."
                        data-testid="input-content-body"
                      />
                      <p className="text-xs text-muted-foreground">
                        This question will appear in the user's wellness
                        workbook
                      </p>
                    </div>
                  )}

                  {/* Quote */}
                  {selectedType === "quote" && (
                    <div className="space-y-2">
                      <Label htmlFor="body">Quote Text</Label>
                      <Textarea
                        id="body"
                        name="body"
                        rows={3}
                        required
                        defaultValue={editingContent?.body || ""}
                        placeholder="Enter the inspirational quote..."
                        data-testid="input-content-body"
                      />
                      <p className="text-xs text-muted-foreground">
                        Add attribution in the description field if needed
                      </p>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Coach and Metadata */}
                <div className="space-y-2">
                  <Label htmlFor="coachId">Coach</Label>
                  <Select
                    name="coachId"
                    defaultValue={editingContent?.coachId || ""}
                  >
                    <SelectTrigger data-testid="select-content-coach">
                      <SelectValue placeholder="Select a coach" />
                    </SelectTrigger>
                    <SelectContent>
                      {coaches?.map((coach) => (
                        <SelectItem key={coach.id} value={coach.id}>
                          {coach.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tags">Tags</Label>
                  <Input
                    id="tags"
                    name="tags"
                    defaultValue={editingContent?.tags?.join(", ") || ""}
                    placeholder="meditation, calm, morning (comma-separated)"
                    data-testid="input-content-tags"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    name="status"
                    defaultValue={editingContent?.status || "draft"}
                  >
                    <SelectTrigger data-testid="select-content-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {contentStatuses.map((status) => (
                        <SelectItem
                          key={status}
                          value={status}
                          className="capitalize"
                        >
                          {status.replace(/_/g, " ")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetDialogState}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      createMutation.isPending ||
                      updateMutation.isPending ||
                      completeUploadMutation.isPending ||
                      (!editingContent &&
                        (selectedType === "audio" ||
                          selectedType === "video") &&
                        !isUploadComplete)
                    }
                    data-testid="button-submit-content"
                  >
                    {createMutation.isPending ||
                    updateMutation.isPending ||
                    completeUploadMutation.isPending
                      ? "Saving..."
                      : editingContent
                        ? "Update"
                        : "Create"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </section>
    </div>
  );
}
